import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Calendar, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { PassportSectionFallback } from './PassportSectionFallback';
import { parseDateOnly } from '@/lib/passport/dateOnly'; // Core-100 R4 W1: date-only facts in the local calendar

interface EmploymentRecord {
  id: string;
  start_date: string;
  end_date?: string | null;
  shop?: { name: string } | null;
  /** Core-100 CORE-D: employer name recorded with the fact; survives deletion of the shop. */
  shop_name_snapshot?: string | null;
  /** Core-100 CORE-D: set when the employer's shop record was deleted. */
  shop_deleted_at?: string | null;
}

interface PassportEmploymentHistoryProps {
  employmentHistory: EmploymentRecord[];
  /**
   * The read FAILED, as opposed to returning nothing.
   *
   * CORE-C: "MUST NOT present its own failure to retrieve as a statement about
   * the subject." Without this, a failed fetch arrived here as `[]` and the
   * person was told "No employment history available" — a claim about their
   * working life manufactured out of a network error. Optional and defaulting
   * to false so callers that genuinely have no error are unaffected.
   */
  isError?: boolean;
}

export function PassportEmploymentHistory({
  employmentHistory,
  isError = false,
}: PassportEmploymentHistoryProps) {
  // Checked BEFORE the empty branch: an empty array is what a failed read looks
  // like by the time it reaches a component, so order is the whole fix.
  if (isError) {
    return <PassportSectionFallback sectionName="Employment History" />;
  }

  if (!employmentHistory || employmentHistory.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            Employment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">No employment history available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-muted-foreground" />
          Employment History
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {employmentHistory.map((emp, index) => (
            <div 
              key={emp.id} 
              className={`flex items-center gap-4 p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors ${
                index === 0 ? 'border-l-4 border-l-primary' : ''
              }`}
            >
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-foreground truncate">
                  {emp.shop?.name || emp.shop_name_snapshot || 'Unknown Shop'}
                  {emp.shop_deleted_at && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground" data-testid="employment-shop-closed">(shop closed)</span>
                  )}
                </p>
                {/* Core-100 R4 CORE-C/CORE-D: this record is the platform's OBSERVATION of employment (written by
                    OptiBay when the person was a member of the shop), shown as observation: not the person's claim and
                    not an employer's attestation. */}
                <p
                  className="text-[11px] text-muted-foreground"
                  data-testid="employment-provenance"
                  title="Observed by OptiBay from this person's membership of the shop. It is not a self-reported entry and not an employer's confirmation."
                >
                  Recorded by OptiBay (observed membership)
                </p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                  <Calendar className="h-3.5 w-3.5" />
                  <span>{parseDateOnly(emp.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  <ChevronRight className="h-3 w-3" />
                  <span>
                    {emp.end_date 
                      ? parseDateOnly(emp.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'Present'
                    }
                  </span>
                </div>
              </div>

              {!emp.end_date && (
                <Badge variant="default" className="flex-shrink-0">
                  Current
                </Badge>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
